import axios from 'axios';
import momentTZ from 'moment-timezone';
import Cookies from 'universal-cookie';
import {
  clearAuth,
  getAuth,
  getActiveRole,
  hasAdminAccess,
  loadSession,
  passwordSignedIn
} from './auth';
import { getConstantsFromCache, ConstantsType } from './hooks/useConstants';

const oas3Api = axios.create({
  baseURL: 'https://dev.mesn.arl-nus.org/service',
  headers: {
    'Content-Type': 'application/json'
  }
});

export { oas3Api };

const api = axios.create({
  baseURL: import.meta.env.VITE_API_URL,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json'
  }
});

// Cookie and interceptor references: https://axios-http.com/docs/req_config
// https://axios-http.com/docs/interceptors
api.interceptors.request.use(
  async (config: any) => {
    await loadSession();
    const session = getAuth().session;
    if (session) config.headers['X-CSRF-Token'] = session.csrfToken;
    const cookies = new Cookies();
    const tokens = cookies.get('admin-tokens');
    const currRole = getActiveRole();
    if (tokens && !session) {
      const { accessToken } = tokens;
      config.headers.Authorization = `Bearer ${accessToken}`;
    }
    if (currRole) {
      config.headers['X-Active-Role'] = currRole;
    }
    return config;
  },
  (err) => Promise.reject(err)
);

// Add a response interceptor
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error;
    // NUS sessions expire independently of the legacy password refresh-token flow.
    if (
      error.response?.status === 401 &&
      getAuth().session?.status === 'authenticated'
    ) {
      clearAuth();
      window.location.assign('/login');
      return Promise.reject(error);
    }

    if (
      originalRequest.response?.status === 401 &&
      originalRequest.response?.data?.message === 'Token Expired'
    ) {
      const cookies = new Cookies();
      try {
        const tokens = cookies.get('admin-tokens');
        if (tokens) {
          const refreshRes = await axios.post(
            import.meta.env.VITE_API_URL + '/public/refreshAccessToken',
            { refreshToken: tokens.refreshToken }
          );
          passwordSignedIn(refreshRes.data);
          // Retry the original request with the new token
          originalRequest.config.headers.Authorization = `Bearer ${refreshRes.data.accessToken}`;
          return axios(originalRequest.config);
        }
      } catch (error) {
        // Handle refresh token error or redirect to login
        console.log(error);
        clearAuth();
        window.alert('Token Expired. Please login');
        // @ts-ignore
        window.location = '/login';
      }
    }

    if (
      originalRequest.response?.status === 409 &&
      originalRequest.response?.data?.message === 'Force Access Token Refresh'
    ) {
      const cookies = new Cookies();
      try {
        const tokens = cookies.get('admin-tokens');
        if (tokens) {
          const refreshRes = await axios.post(
            import.meta.env.VITE_API_URL + '/public/refreshAccessToken',
            { refreshToken: tokens.refreshToken, forced: true }
          );
          passwordSignedIn(refreshRes.data);
          // Retry the original request with the new token
          originalRequest.config.headers.Authorization = `Bearer ${refreshRes.data.accessToken}`;
          return axios(originalRequest.config);
        }
      } catch (error) {
        // Handle refresh token error or redirect to login
        console.log(error);
        clearAuth();
        window.alert('Token Expired. Please login');
        // @ts-ignore
        window.location = '/login';
      }
    }

    return Promise.reject(error);
  }
);

// The backend selects NUS or password authentication; the portal never decides by domain.
export async function continueWithEmail(email: string): Promise<boolean> {
  if (getAuth().session) {
    await api.post('/public/oidc/logout');
    clearAuth();
  }
  const normalized = email.trim().toLowerCase();
  const { data } = await api.post('/public/auth/method', { email: normalized });
  if (data.method === 'password') return true;
  if (data.method !== 'nus') throw new Error('Invalid authentication method');
  const response = await api.post('/public/oidc/start', {
    email: normalized,
    portal: 'admin'
  });
  window.location.assign(response.data.authorizationUrl);
  return false;
}

export default api;

export const updateUserStatus = async (
  userId: number | string,
  isActive: boolean
) => {
  const response = await api.patch(`/admin/users/${userId}/status`, {
    isActive
  });
  return response.data;
};

export const fetchMesnParameters = async () => {
  if (hasAdminAccess(getAuth().user)) {
    const params = await api.get('/public/getMesnParameters');
    return params.data;
  }
  return {};
};

export const fetchParameters = async () => {
  if (hasAdminAccess(getAuth().user)) {
    const params = await api.get('/public/parameters');
    return params.data;
  }
  return {};
};

export const fetchParamsWithPermissions = async (userId: any) => {
  if (hasAdminAccess(getAuth().user)) {
    const params = await api.get(`/admin/parametersWithPermissions/${userId}`);
    return params.data;
  }
  return {};
};

export const queryLineData = async (start: any, end: any, card: any) => {
  const { LOCATION_FULL_FORM_TO_ACRONYM_MAP } =
    getConstantsFromCache() as ConstantsType;
  const response = await api.post(
    `${import.meta.env.VITE_API_URL}/api/rawData/line`,
    {
      to: end,
      from: start,
      location: LOCATION_FULL_FORM_TO_ACRONYM_MAP[card.location],
      depth: card.depth.value,
      parameterDesc: card.parameterDesc,
      sensorDetailsHash: card.sensorDetailsHash
    }
  );

  const data = response.data.map((obj: any, index: any) => {
    return {
      x: momentTZ.utc(Number(obj.timestamp)).tz('Asia/Singapore'),
      y: obj.qcValue,
      qcFlag: obj.qcFlag
    };
  });
  // @ts-ignore
  return {
    data,
    accuracy: card.accuracy,
    isCruiseParam: card.isCruiseParam,
    rawData: response.data
  };
};

export const queryBoxData = async (
  start: any,
  end: any,
  card: any,
  interval: any
) => {
  const { LOCATION_FULL_FORM_TO_ACRONYM_MAP } =
    getConstantsFromCache() as ConstantsType;
  const response = await api.post(
    `${import.meta.env.VITE_API_URL}/api/rawData/box`,
    {
      to: end,
      from: start,
      location: LOCATION_FULL_FORM_TO_ACRONYM_MAP[card.location],
      depth: card.depth.value,
      parameterDesc: card.parameterDesc,
      sensorDetailsHash: card.sensorDetailsHash,
      interval
    }
  );
  // @ts-ignore
  return {
    data: response.data,
    accuracy: card.accuracy,
    isCruiseParam: card.isCruiseParam,
    rawData: response.data
  };
};

export const getQuantity = (quantity: any): string => {
  return quantity.toLowerCase().replace(/\s/g, '-');
};

export const getConstants = async () => {
  return api({
    method: 'get',
    url: `${import.meta.env.VITE_API_URL}/public/constants`,
    dontShowGlobalModal: true
  } as any).then((res) => {
    return res.data;
  });
};
